import { useEffect, useLayoutEffect, useCallback, useRef } from 'react'
import ChampField     from './fields/ChampField'
import ComboField     from './fields/ComboField'
import CanvasField    from './fields/CanvasField'
import TabsField      from './fields/TabsField'
import TableField     from './fields/TableField'
import TreeField      from './fields/TreeField'
import LabelField     from './fields/LabelField'
import MultiFileField from './fields/MultiFileField'
import AccordionField from './fields/AccordionField'
import WidgetField    from './fields/WidgetField'
import FieldsetField  from './fields/FieldsetField'
import { toStyleObject } from '@/lib/utils'

// eslint-disable-next-line react-refresh/only-export-components
export function countAsyncFields(fields) {
  if (!fields) return 0
  let count = 0
  for (const field of Object.values(fields)) {
    if (field.type === 15 || field.type === 16) count++
    else if (field.type === 1 || field.type === 20) count += countAsyncFields(field.fields)
    else if (field.type === 11 || field.type === 12) {
      for (const el of (field.elements ?? [])) count += countAsyncFields(el.fields)
    }
  }
  return count
}

const RENDERERS = {
  1:  CanvasField,
  2:  ChampField,
  4:  ComboField,
  8:  LabelField,
  11: TabsField,
  12: AccordionField,
  15: TableField,
  16: TreeField,
  19: MultiFileField,
  20: CanvasField,
  22: FieldsetField,
  23: WidgetField,
}

function normaliseFields(fields) {
  return fields ? Object.values(fields) : []
}

// eslint-disable-next-line react-refresh/only-export-components
export function resolveLayout(obj) {
  if (obj?.layout?.type === 'grid') return obj.layout.grid
  return obj?.gridLayout ?? null
}

// Seuls les conteneurs (CanvasField, TabsField, AccordionField) invoquent onRenderFields.
// Ne l'attacher qu'à ceux-là : les autres types (notamment TableField/TreeField, coûteux à
// reconcilier) reçoivent ainsi des props stables d'un rendu à l'autre, ce qui permet à
// React.memo de les court-circuiter (cf. slowdown observé au clic sur les onglets).
const CONTAINER_TYPES = new Set([1, 11, 12, 20])

function renderOne(field, onEvent, onDataLoaded, loaders) {
  try {
    const Component = RENDERERS[field.type]
    if (!Component) {
      console.error(`[ScreenGenerator] Type de composant inconnu (${field.type}) pour le champ "${field.id}"`)
      return null
    }
    const props = {
      ...field,
      gridLayout: resolveLayout(field),
      onEvent,
      // ovEvent/ovLoaders : overrides optionnels, utilisés par CanvasField.addElements() pour
      // rendre des champs ajoutés dynamiquement avec leurs propres listeners (mêmes noms que
      // modalDialogBox : eventCallback, dynaComboLoader, gridLoader, treeLoader,
      // gridDragDropCallback, treeDragDropCallback), indépendants de l'onEvent/loaders de l'écran.
      ...(CONTAINER_TYPES.has(field.type) ? { onRenderFields: (f, gl, ovEvent, ovLoaders) => renderFields(f, ovEvent || onEvent, gl, onDataLoaded, ovLoaders || loaders) } : {}),
      ...((field.type === 15 || field.type === 16) ? { onDataLoaded } : {}),
      ...(field.type === 4  ? { dynaComboLoader: loaders?.dynaComboLoader } : {}),
      ...(field.type === 15 ? { gridLoader: loaders?.gridLoader, gridDragDropCallback: loaders?.gridDragDropCallback } : {}),
      ...(field.type === 16 ? { treeLoader: loaders?.treeLoader, treeDragDropCallback: loaders?.treeDragDropCallback } : {}),
    }
    return <Component key={field.id} {...props} />
  } catch (e) {
    console.error(`[ScreenGenerator] Erreur lors de la création du champ "${field?.id}"`, e)
    return null
  }
}

function renderGrid(fields, gridLayout, onEvent, onDataLoaded, loaders) {
  try {
    const { id: gridId, maxRows, maxColumns, gridColumnWidths, positions = {} } = gridLayout
    const map = fields ?? {}

    // Collecte tous les ids référencés dans les positions
    const referenced = new Set()
    for (const row of Object.values(positions)) {
      for (const cell of Object.values(row)) {
        ;(cell.elements ?? []).forEach(id => referenced.add(id))
      }
    }

    // Cellules occupées par un rowspan d'une ligne précédente
    const occupied = new Set()
    const tableRows = []

    for (let r = 1; r <= maxRows; r++) {
      const cells = []
      let c = 1
      while (c <= maxColumns) {
        if (occupied.has(`${r},${c}`)) { c++; continue }

        const cellDef = positions[String(r)]?.[String(c)]

        if (cellDef) {
          const colspan = cellDef.colspan || 1
          const rowspan = cellDef.rowspan || 1

          for (let dr = 0; dr < rowspan; dr++) {
            for (let dc = 0; dc < colspan; dc++) {
              if (dr > 0 || dc > 0) occupied.add(`${r + dr},${c + dc}`)
            }
          }

          // Padding posé sur chaque composant (wrapper dédié par élément), pas sur le <td>
          // lui-même — un <td> sans padding évite un espacement fantôme sur une cellule vide ou
          // en fin de colspan/rowspan, et laisse chaque composant maître de son propre espacement
          // (cohérent avec un éventuel style par composant à l'avenir).
          // `field.visible === false` → wrapper en display:none plutôt que juste paddé : sinon
          // seul le div racine du composant (LabelField notamment, [data-tbs-root]) porte
          // display:none, mais ce wrapper (parent de ce div racine) reste visible et garde son
          // padding — une cellule avec plusieurs libellés cachés affiche alors des espaces
          // fantômes (padding de chaque wrapper resté visible), décalant le contenu réel.
          // `data-tbs-cell-wrapper={elemId}` (posé qu'il soit initialement visible ou non) :
          // permet à show()/hide()/isVisible() (tbsGeneralApi.js) de cibler ce wrapper plutôt que
          // le seul div racine du composant — même piège que ci-dessus, mais déclenché cette fois
          // en cours de vie du composant (API impérative), pas seulement au rendu initial.
          const content = (cellDef.elements ?? []).map(elemId => {
            const field = map[elemId]
            if (!field) {
              console.error(`[ScreenGenerator] Champ introuvable : "${elemId}" (référencé dans le layout en grille mais absent de fields)`)
              return null
            }
            const style = field.visible === false ? { display: 'none' } : { padding: '4px' }
            return <div key={elemId} data-tbs-cell-wrapper={elemId} style={style}>{renderOne(field, onEvent, onDataLoaded, loaders)}</div>
          })

          // Une cellule ne contenant que des libellés (type 8) est centrée verticalement plutôt
          // qu'alignée en haut — un libellé fait une seule ligne, quand la cellule voisine de la
          // même ligne (ex. un ChampField, plus haut : légende + input) impose une hauteur de
          // ligne bien supérieure. `height: 100%` sur le wrapper de la cellule ne fonctionnerait
          // pas ici : un pourcentage de hauteur dans un <td> dont la hauteur reste "auto" (cas
          // normal, hauteur de ligne dictée par le contenu) est traité comme "auto" par les
          // navigateurs (dépendance circulaire) — seul `vertical-align` sur le <td> lui-même
          // fonctionne de façon fiable dans ce cas.
          const cellElements = (cellDef.elements ?? []).map(elemId => map[elemId])
          const onlyLabels = cellElements.length > 0 && cellElements.every(f => f?.type === 8)

          cells.push(
            <td
              key={c}
              id={cellDef.id || undefined}
              colSpan={colspan > 1 ? colspan : undefined}
              rowSpan={rowspan > 1 ? rowspan : undefined}
              className={cellDef.class || undefined}
              // `cellDef.style` (JSON) fusionné après verticalAlign/textAlign calculés : une valeur
              // explicite dans le JSON prime sur ces deux valeurs par défaut plutôt que l'inverse.
              style={{ verticalAlign: onlyLabels ? 'middle' : 'top', textAlign: 'left', ...toStyleObject(cellDef.style) }}
            >
              {content}
            </td>
          )
          c += colspan
        } else {
          cells.push(<td key={c} />)
          c++
        }
      }
      tableRows.push(<tr key={r}>{cells}</tr>)
    }

    // Champs présents dans fields mais absents du gridLayout
    for (const id of Object.keys(map)) {
      if (!referenced.has(id)) {
        console.error(`[ScreenGenerator] Champ "${id}" présent dans fields mais absent du layout en grille`)
      }
    }

    return (
      <table
        id={gridId || undefined}
        className={gridLayout.class || undefined}
        // `gridLayout.style` (JSON) fusionné après les styles structurels par défaut — une valeur
        // explicite dans le JSON prime sur ces valeurs par défaut plutôt que l'inverse.
        style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', ...toStyleObject(gridLayout.style) }}
      >
        {gridColumnWidths && (
          <colgroup>
            {gridColumnWidths.map((w, i) => <col key={i} style={{ width: w }} />)}
          </colgroup>
        )}
        <tbody>{tableRows}</tbody>
      </table>
    )
  } catch (e) {
    console.error('[ScreenGenerator] Erreur lors de la génération du layout en grille', e)
    return null
  }
}

// eslint-disable-next-line react-refresh/only-export-components
export function renderFields(fields, onEvent, gridLayout = null, onDataLoaded = null, loaders = null) {
  if (gridLayout) return renderGrid(fields, gridLayout, onEvent, onDataLoaded, loaders)

  return normaliseFields(fields).map(field =>
    renderOne(field, onEvent, onDataLoaded, loaders)
  )
}

export default function ScreenGenerator({ descriptor, onEvent }) {
  const pendingRef  = useRef(countAsyncFields(descriptor?.fields ?? {}))
  const firedRef    = useRef(false)
  const onEventRef  = useRef(onEvent)
  const screenIdRef = useRef(descriptor?.id)
  useLayoutEffect(() => { onEventRef.current = onEvent })

  useEffect(() => {
    // Groupes de checkboxes (radio-like) : chaque groupe est un Set d'IDs
    const _groups = []

    window.tbsGlobalForm = {
      get form() { return document.forms['tbsGenericComponentFrm'] ?? null },

      validate() {
        // Efface les marqueurs d'erreur précédents sur les déclencheurs
        document.querySelectorAll('[data-tbs-tab-trigger-id][data-tbs-error]').forEach(el => {
          el.removeAttribute('data-tbs-error')
          el.style.removeProperty('text-decoration')
        })
        document.querySelectorAll('[data-tbs-accordion-trigger-id][data-tbs-error]').forEach(el => {
          el.removeAttribute('data-tbs-error')
          el.style.removeProperty('text-decoration')
        })

        let allValid = true
        for (const [compId, comp] of Object.entries(window.tbsComponents ?? {})) {
          if (typeof comp.validate !== 'function') continue
          if (comp.validate()) continue

          allValid = false
          // Remonte le DOM depuis le composant pour marquer tous les onglets / sections ancêtres
          const root = document.querySelector(`[data-tbs-root="${compId}"]`)
          if (!root) continue
          let el = root.parentElement
          while (el && el !== document.body) {
            if (el.hasAttribute('data-tbs-tab-panel-id')) {
              const trigger = document.querySelector(`[data-tbs-tab-trigger-id="${el.getAttribute('data-tbs-tab-panel-id')}"]`)
              if (trigger) { trigger.setAttribute('data-tbs-error', 'true'); trigger.style.textDecoration = 'underline wavy hsl(var(--destructive))' }
            }
            if (el.hasAttribute('data-tbs-accordion-panel-id')) {
              const trigger = document.querySelector(`[data-tbs-accordion-trigger-id="${el.getAttribute('data-tbs-accordion-panel-id')}"]`)
              if (trigger) { trigger.setAttribute('data-tbs-error', 'true'); trigger.style.textDecoration = 'underline wavy hsl(var(--destructive))' }
            }
            el = el.parentElement
          }
        }
        return allValid
      },

      submit() {
        if (!this.validate()) return false
        this.form?.submit()
        return true
      },

      // Crée un groupe radio-like de checkboxes (type 2 masque 115)
      groupCheckboxes(...checkboxIds) {
        const newSet = new Set(checkboxIds)
        // Vérifie l'intersection avec les groupes existants
        for (const group of _groups) {
          for (const cbId of newSet) {
            if (group.has(cbId)) throw new Error(`groupCheckboxes: l'ID "${cbId}" appartient déjà à un groupe`)
          }
        }
        _groups.push(newSet)

        // Ajoute un listener onChange sur chaque checkbox du groupe
        for (const cbId of checkboxIds) {
          const comp = window.tbsComponents?.[cbId]
          if (!comp?.addListener) continue
          comp.addListener('onChange', (srcId, eventType, newVal) => {
            if (srcId !== cbId || eventType !== 'onChange') return
            if (newVal === 'true' || newVal === true) {
              // Décoche toutes les autres cases du groupe et émet onChange pour chacune
              for (const otherId of newSet) {
                if (otherId === cbId) continue
                const otherComp = window.tbsComponents?.[otherId]
                if (!otherComp) continue
                const wasChecked = otherComp.getValue?.() === 'true' || otherComp.isChecked?.()
                if (wasChecked) {
                  otherComp.setValue('false')
                  onEventRef.current(otherId, 'onChange', 'false')
                }
              }
            }
          })
        }
      },
    }
    // window.tbsGlobalForm est pré-déclaré via `var` sur window côté JSP/index.html (non-configurable) —
    // `delete` lèverait une TypeError en mode strict, voir App.jsx.
    return () => { window.tbsGlobalForm = undefined }
  }, [])

  const onDataLoaded = useCallback(() => {
    if (firedRef.current) return
    pendingRef.current = Math.max(0, pendingRef.current - 1)
    if (pendingRef.current === 0) {
      firedRef.current = true
      onEventRef.current(screenIdRef.current, 'onLoad')
    }
  }, [])

  useEffect(() => {
    if (pendingRef.current === 0 && !firedRef.current) {
      firedRef.current = true
      onEvent(descriptor.id, 'onLoad')
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  if (!descriptor) return null
  // eslint-disable-next-line react-hooks/refs
  const screenFields = renderFields(descriptor.fields, onEvent, resolveLayout(descriptor), onDataLoaded)
  return (
    <form name={window.tbsGenericComponentFrmInfos.name} action={window.tbsGenericComponentFrmInfos.action} method={window.tbsGenericComponentFrmInfos.method} enctype={window.tbsGenericComponentFrmInfos.enctype}>
      {Object.entries(window.tbsGenericComponentFrmInfos.attributes).map(([key, value]) => {return <input type="hidden" key={key} name={key} value={value} />})}
      {screenFields}
    </form>
  )
}
